import { TrendingUp, Shield } from 'lucide-react'
import type { Strategy } from '../data/scenarios'
import type { ScenarioMetrics } from '../data/metricsData'

interface StrategyOptionCardProps {
  strategy: Strategy
  metrics: ScenarioMetrics
  selected: boolean
  disabled: boolean
  onSelect: () => void
}

const STRATEGY_CONFIG: Record<Strategy, {
  label: string
  tagline: string
  icon: React.ReactNode
  accentColor: string
  borderSelected: string
  bgSelected: string
}> = {
  max_yield: {
    label: 'Max Yield',
    tagline: 'Dense packing · Maximum revenue',
    icon: <TrendingUp className="w-4 h-4" />,
    accentColor: 'text-emerald-300',
    borderSelected: 'border-emerald-400/60',
    bgSelected: 'bg-emerald-950/40',
  },
  op_ease: {
    label: 'Operational Ease',
    tagline: 'Wide corridors · Minimal crew',
    icon: <Shield className="w-4 h-4" />,
    accentColor: 'text-sky-300',
    borderSelected: 'border-sky-400/60',
    bgSelected: 'bg-sky-950/40',
  },
}

export function StrategyOptionCard({
  strategy,
  metrics,
  selected,
  disabled,
  onSelect,
}: StrategyOptionCardProps) {
  const cfg = STRATEGY_CONFIG[strategy]

  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`w-full text-left rounded-xl border p-4 transition-all
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${selected
          ? `${cfg.borderSelected} ${cfg.bgSelected}`
          : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
        }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`${cfg.accentColor}`}>{cfg.icon}</div>
        <div className={`font-bold text-sm ${selected ? cfg.accentColor : 'text-white'}`}>
          {cfg.label}
        </div>
        {selected && (
          <div className={`ml-auto text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cfg.bgSelected} ${cfg.accentColor} border ${cfg.borderSelected}`}>
            Selected
          </div>
        )}
      </div>
      <div className="text-xs text-white/50 mb-3">{cfg.tagline}</div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Revenue</span>
          <span className="text-emerald-300 font-semibold">+${metrics.revenueImpact.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Tow moves</span>
          <span className={`font-semibold ${metrics.towMoves > 5 ? 'text-amber-300' : 'text-white'}`}>
            {metrics.towMoves}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Clearance</span>
          <span className={`font-semibold ${metrics.safetyClearanceFt < 8 ? 'text-amber-300' : 'text-white'}`}>
            {(metrics.safetyClearanceFt * 0.3048).toFixed(1)}m
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-white/40">Crew</span>
          <span className={`font-semibold ${metrics.crewRequired > 4 ? 'text-amber-300' : 'text-white'}`}>
            {metrics.crewRequired} staff
          </span>
        </div>
      </div>
    </button>
  )
}
